// EtapeRemcCard — Composant réutilisable pour afficher une étape de progression
// (1 des 23) avec un accordéon dépliable montrant le contenu REMC de la leçon
// (Pourquoi / Comment / Risques + points clés + erreurs). Utilisé sur :
//   - Fiches de séance publiques
//   - Examens blancs publics
//   - Page /cours/progression (bientôt)

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, BookOpen, Target, ShieldAlert, Sparkles, Zap,
  AlertTriangle, ArrowRight, Clock, ListChecks,
} from 'lucide-react';
import { COURS_VOITURE_ELEVE } from '../data/coursVoitureEleve';
import { PROGRESSION } from '../data/progressionAutoEcole';
import { toTu, toTuFlat } from '../lib/toTu';

// Map des couleurs par phase
const PHASE_COLOR = {
  Mécanique:              { badge: 'text-red-300 border-red-500/40 bg-red-500/10',       accent: 'text-red-400' },
  Observation:            { badge: 'text-amber-300 border-amber-500/40 bg-amber-500/10', accent: 'text-amber-400' },
  'Préparation permis':   { badge: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', accent: 'text-emerald-400' },
};

export function findEtape(id) {
  for (const phase of PROGRESSION) {
    const s = phase.steps.find((st) => st.id === id);
    if (s) return { ...s, phase: phase.phase, phaseSlug: phase.slug, phaseColor: phase.color };
  }
  return null;
}

export function findLessonByCode(code) {
  if (!code) return null;
  const m = String(code).match(/^(C\d+)([a-z])$/i);
  if (!m) return null;
  const [, compCode, letter] = m;
  const comp = COURS_VOITURE_ELEVE.find((c) => c.code === compCode);
  if (!comp) return null;
  const l = comp.lecons.find((x) => x.letter === letter.toLowerCase());
  if (!l) return null;
  return { ...l, compCode, compTitre: comp.title || comp.titre };
}

export default function EtapeRemcCard({ etapeId, defaultOpen = false, status = null, testid }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const etape = useMemo(() => findEtape(etapeId), [etapeId]);
  const lesson = useMemo(() => (etape ? findLessonByCode(etape.remc) : null), [etape]);

  if (!etape) return null;
  const colors = PHASE_COLOR[etape.phase] || PHASE_COLOR.Mécanique;
  const remc = lesson?.remc || {};

  return (
    <article className="rounded-xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid={testid || `etape-card-${etape.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition"
        data-testid={`etape-toggle-${etape.id}`}
      >
        <span className={`shrink-0 h-8 min-w-[44px] px-2 rounded-md border ${colors.badge} font-mono-spec text-[11.5px] font-bold inline-flex items-center justify-center`}>
          {etape.id}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-white text-[13.5px] leading-snug font-semibold">{etape.label}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-white/50 font-mono-spec">
            <span className={colors.accent}>{etape.phase}</span>
            {etape.remc && <span className="text-white/40">· REMC {etape.remc}</span>}
            {status && (
              <span className={`px-1.5 py-0.5 rounded text-[10.5px] font-bold ${
                status === 'acquis' ? 'bg-emerald-600/25 text-emerald-300'
                : status === 'en_cours' ? 'bg-sky-600/25 text-sky-300'
                : status === 'a_revoir' ? 'bg-amber-600/25 text-amber-300'
                : 'bg-white/10 text-white/50'
              }`}>
                {status === 'acquis' ? 'Acquise' : status === 'en_cours' ? 'En cours' : status === 'a_revoir' ? 'À revoir' : 'Non abordée'}
              </span>
            )}
          </div>
        </div>
        <ChevronDown size={16} className={`shrink-0 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && lesson && (
        <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3" data-testid={`etape-remc-${etape.id}`}>
          {/* Objectif */}
          {lesson.objectif && (
            <div>
              <div className="text-sky-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><Target size={10} /> Objectif</div>
              <p className="text-white/85 text-[13px] leading-relaxed">{toTu(lesson.objectif)}</p>
            </div>
          )}

          {/* Pourquoi */}
          {remc.pourquoi?.intro && (
            <div className="rounded-lg border border-emerald-500/25 bg-emerald-950/15 p-3">
              <div className="text-emerald-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><Sparkles size={10} /> POURQUOI apprendre ça ?</div>
              <p className="text-white/85 text-[13px] leading-relaxed whitespace-pre-wrap">{toTu(remc.pourquoi.intro)}</p>
              {remc.pourquoi.avantages?.length > 0 && (
                <ul className="mt-2 space-y-1 text-white/80 text-[12.5px]">
                  {remc.pourquoi.avantages.slice(0, 4).map((a, i) => <li key={i} className="flex gap-2"><span className="text-emerald-400 shrink-0">✓</span> {toTuFlat(a)}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Comment */}
          {remc.comment && (remc.comment.a_traiter || remc.comment.savoir_faire?.length > 0 || remc.comment.savoir?.length > 0) && (
            <div className="rounded-lg border border-sky-500/25 bg-sky-950/15 p-3">
              <div className="text-sky-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><Zap size={10} /> COMMENT s&apos;y prendre</div>
              {remc.comment.a_traiter && <p className="text-white/85 text-[13px] leading-relaxed mb-2">{toTu(remc.comment.a_traiter)}</p>}
              {remc.comment.savoir_faire?.length > 0 && (
                <ul className="mt-1 space-y-1 text-white/80 text-[12.5px]">
                  {remc.comment.savoir_faire.slice(0, 5).map((a, i) => <li key={i} className="flex gap-2"><span className="text-sky-400 shrink-0">→</span> {toTuFlat(a)}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Risques */}
          {remc.risques && (remc.risques.intro || (Array.isArray(remc.risques.risques_listes) && remc.risques.risques_listes.length > 0)) && (
            <div className="rounded-lg border border-red-500/30 bg-red-950/15 p-3">
              <div className="text-red-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><ShieldAlert size={10} /> RISQUES à connaître</div>
              {remc.risques.intro && <p className="text-white/85 text-[13px] leading-relaxed mb-2">{toTu(remc.risques.intro)}</p>}
              {Array.isArray(remc.risques.risques_listes) && remc.risques.risques_listes.length > 0 && (
                <ul className="space-y-1 text-white/80 text-[12.5px]">
                  {remc.risques.risques_listes.slice(0, 5).map((r, i) => <li key={i} className="flex gap-2"><span className="text-red-400 shrink-0">⚠</span> {toTuFlat(r)}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Points clés + erreurs — compact */}
          {(lesson.points_cles?.length > 0 || lesson.erreurs_a_eviter?.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-3">
              {lesson.points_cles?.length > 0 && (
                <div className="rounded-lg border border-amber-500/25 bg-amber-950/10 p-3">
                  <div className="text-amber-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><ListChecks size={10} /> À mémoriser</div>
                  <ul className="space-y-1 text-white/80 text-[12.5px]">
                    {lesson.points_cles.slice(0, 4).map((p, i) => <li key={i} className="flex gap-2"><span className="text-amber-400 shrink-0">★</span> {toTuFlat(p)}</li>)}
                  </ul>
                </div>
              )}
              {lesson.erreurs_a_eviter?.length > 0 && (
                <div className="rounded-lg border border-red-500/25 bg-red-950/10 p-3">
                  <div className="text-red-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><AlertTriangle size={10} /> Erreurs à éviter</div>
                  <ul className="space-y-1 text-white/80 text-[12.5px]">
                    {lesson.erreurs_a_eviter.slice(0, 4).map((p, i) => <li key={i} className="flex gap-2"><span className="text-red-400 shrink-0">✗</span> {toTuFlat(p)}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Lien leçon complète */}
          <div className="flex items-center gap-2 pt-1">
            <Link
              to={`/cours/eleve/voiture?remc=${etape.remc}`}
              data-testid={`etape-lesson-link-${etape.id}`}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-[11.5px] font-semibold"
            >
              <BookOpen size={11} /> Fiche complète {etape.remc} <ArrowRight size={11} />
            </Link>
            {lesson.duree_estimee && (
              <span className="text-white/40 text-[11px] font-mono-spec inline-flex items-center gap-1">
                <Clock size={10} /> {lesson.duree_estimee}
              </span>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
